import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { AccessGroup } from '../../models/accessGroup';

import { HttpErrorResponse } from '@angular/common/http';
import { GroupRepository } from '../../repositories/group/group.repository';

@Injectable({
    providedIn: 'root',
})
export class GroupService {
    constructor(private repo: GroupRepository) {}

    getUserGroups(): Observable<AccessGroup[]> {
        return this.repo.getGroupsForCurrentUser();
    }

    isUserAdmin(groups: string[]): boolean {
        if (groups && groups.length > 0 && (groups.indexOf('Assistant') > -1 || groups.indexOf('Manager') > -1)) {
            return true;
        }
        return false;
    }

    isUserSuperAdmin(groups: string[]): boolean {
        if (groups && groups.length > 0 && groups.indexOf('Manager') > -1) {
            return true;
        }
        return false;
    }
    getIsUserAdmin$() {
        return this.groupsSubject.pipe(map((groups) => this.isUserAdmin(groups)));
    }
    getIsUserSuperAdmin$() {
        return this.groupsSubject.pipe(map((groups) => this.isUserSuperAdmin(groups)));
    }
    private groupsSubject = new ReplaySubject<string[]>(1);
    public readonly isAdmin$ = this.groupsSubject.pipe(
        map((groups) => this.isUserAdmin(groups)),
        shareReplay(1)
    );
    public readonly isSuperAdmin$ = this.groupsSubject.pipe(
        map((groups) => this.isUserSuperAdmin(groups)),
        shareReplay(1)
    );
    getGroupsFromStorage(): Observable<string[]> {
        let subject = new BehaviorSubject<string[]>([]);
        const groups = sessionStorage.getItem('grp')?.split(',');

        if (!groups || groups.length === 0) {
            return this.repo.getGroupsForCurrentUser().pipe(
                catchError((error: HttpErrorResponse) => {
                    if (error.status === 401) {
                        // We can ignore such response, because we use this paticular API call to check if front-end has a valid token in trial-and-error manner
                        console.info(
                            'User is not authorized to get groups. Most likely, the user has simply not logged in yet.'
                        );
                        return of([]);
                    } else if (error.status === 404) {
                        // Backend responds with 404 if the token has proper signature, but doesn't carry enough information to allow us match the request with a specific user.
                        // This happens e.g. when signing in with Medpace admin account (logins like `firstname.s@medpace.medpace.com`) which generate tokens without `email` claim.
                        console.info(
                            `Backend responded with 404 when asked for user's roles. Assuming no role is assigned.`
                        );
                        return of([]);
                    }

                    return of([]);
                }),
                map((groups) => groups.map((g) => g.appName)),
                tap((groups) => this.groupsSubject.next(groups))
            );
        } else {
            this.groupsSubject.next(groups);
            subject.next(groups);
            return subject.asObservable();
        }
    }

    setGroupsToSessionStorage(groups: string[]): void {
        this.groupsSubject.next(groups);
        sessionStorage.setItem('grp', groups.toString());
    }
}
